import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, Trash2, IndianRupee, ChevronLeft, ChevronRight, Check,
  Settings, X, RefreshCw, Repeat, AlertTriangle, Users as UsersIcon,
  TrendingUp, Building2, Receipt, Upload, FileText, ExternalLink, Download,
  Calendar, Pencil, Search,
} from 'lucide-react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';

const INR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const iso = (d) => {
  // Local-date YYYY-MM-DD (avoid toISOString() which shifts to UTC and rolls back a day in IST)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// ── Month helpers ────────────────────────────────────────────────────────────
const monthLabel = (d) => d.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const startOfMonthGrid = (d) => {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const wd = first.getDay(); // 0 = Sun
  return new Date(first.getFullYear(), first.getMonth(), 1 - wd);
};

export default function CashFlow() {
  const [user, setUser] = useState(authService.getCurrentUser());
  const isAdmin = user?.role === 'admin';
  const isFC = !!user?.is_finance_controller;
  const canGlobal = isAdmin || isFC;
  const { section } = useParams();
  const navigate = useNavigate();

  // Section derived from URL — no local tab state
  const rawSection = section || 'calendar';
  const tab = ['calendar', 'vendors', 'reimbursements', 'salaries', 'admin', 'consolidated', 'audit'].includes(rawSection) ? rawSection : 'calendar';
  useEffect(() => {
    if (tab === 'admin' && !isAdmin) navigate('/cashflow', { replace: true });
    if (tab === 'consolidated' && !canGlobal) navigate('/cashflow', { replace: true });
    if (tab === 'audit' && !isAdmin) navigate('/cashflow', { replace: true });
  }, [tab, isAdmin, canGlobal, navigate]);

  // refresh user on mount to catch newly-assigned FC role
  useEffect(() => {
    authService.refreshUser().then(u => { if (u) setUser(u); });
  }, []);

  const [cursor, setCursor] = useState(new Date());     // month being viewed
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(canGlobal ? '' : user?.id);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [myLimit, setMyLimit] = useState({ daily_limit: 0, limit_mode: 'soft' });
  const [loading, setLoading] = useState(false);
  const [openDay, setOpenDay] = useState(null);         // 'YYYY-MM-DD'
  const [showCreate, setShowCreate] = useState(false);
  const [editEntry, setEditEntry] = useState(null);      // full entry object being edited
  const [showExtend, setShowExtend] = useState(false);
  const [searchQ, setSearchQ] = useState('');            // calendar search — expense title/vendor/emp/notes
  const [extendSeedDate, setExtendSeedDate] = useState(null);
  const [createSeedDate, setCreateSeedDate] = useState(null);

  const currentMonthKey = monthKey(cursor);
  const effectiveDailyLimit = canGlobal
    ? (summary?.daily_limit || 0)
    : (myLimit.daily_limit || 0);
  const effectiveLimitMode = canGlobal ? (summary?.limit_mode || 'soft') : (myLimit.limit_mode || 'soft');

  // ── initial loads ─────────────────────────────────────────────────────────
  useEffect(() => {
    api.cashflowListCategories().then(r => setCategories(r.data)).catch(() => {});
    api.cashflowListVendors().then(r => setVendors(r.data)).catch(() => {});
    if (canGlobal) {
      api.cashflowListManagers().then(r => {
        setManagers(r.data);
        if (r.data?.length && !selectedManager) setSelectedManager(r.data[0].id);
      }).catch(() => {});
    } else {
      api.cashflowGetMyLimit().then(r => setMyLimit(r.data)).catch(() => {});
    }
  }, [canGlobal]);

  // ── refresh entries + summary when month/manager changes ─────────────────
  const refresh = async () => {
    setLoading(true);
    try {
      const params = { month: currentMonthKey };
      if (canGlobal && selectedManager) params.manager_id = selectedManager;
      const [eRes, sRes] = await Promise.all([
        api.cashflowListEntries(params),
        api.cashflowSummary(params),
      ]);
      setEntries(eRes.data || []);
      setSummary(sRes.data);
    } catch (e) {
      toast.error('Failed to load Cash Flow');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (tab === 'calendar') refresh(); }, [currentMonthKey, selectedManager, tab]);

  // ── grouped entries by date ──────────────────────────────────────────────
  const entriesByDay = useMemo(() => {
    const map = {};
    for (const e of entries) {
      (map[e.planned_date] = map[e.planned_date] || []).push(e);
    }
    return map;
  }, [entries]);

  // ── per-day effective limits (from summary.days) ─────────────────────────
  const effectiveByDay = useMemo(() => {
    const m = {};
    (summary?.days || []).forEach(d => { m[d.date] = d; });
    return m;
  }, [summary]);

  // ── Calendar search ──────────────────────────────────────────────────────
  const searchMatches = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return { entries: [], dates: new Set() };
    const matches = entries.filter(e => {
      const fields = [
        e.title, e.category_name, e.vendor_name, e.notes,
        e.emp_name, e.emp_code, e.bank_name, e.account_no, e.beneficiary_name,
      ];
      return fields.some(v => v && String(v).toLowerCase().includes(q));
    });
    return { entries: matches, dates: new Set(matches.map(m => m.planned_date)) };
  }, [searchQ, entries]);

  // ── grid cells (6 rows × 7 cols) ─────────────────────────────────────────
  const gridStart = startOfMonthGrid(cursor);
  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });

  // ── handlers ─────────────────────────────────────────────────────────────
  const changeMonth = (delta) => {
    const d = new Date(cursor);
    d.setMonth(d.getMonth() + delta);
    setCursor(d);
  };

  const openCreateForDate = (dateIso) => {
    setCreateSeedDate(dateIso);
    setShowCreate(true);
  };

  const toggleDone = async (entry, paidOn) => {
    try {
      await api.cashflowToggleDone(entry.id, paidOn);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to toggle');
    }
  };

  const deleteEntry = async (entry, cascade) => {
    if (!window.confirm(cascade ? 'Delete this and all future recurring copies?' : 'Delete this entry?')) return;
    try {
      await api.cashflowDeleteEntry(entry.id, cascade);
      toast.success('Deleted');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const shiftEntry = async (entry, newDate) => {
    try {
      await api.cashflowUpdateEntry(entry.id, { planned_date: newDate });
      toast.success(`Shifted to ${newDate}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Shift failed');
    }
  };

  const bulkAction = async (payload) => {
    try {
      const res = await api.cashflowBulkAction(payload);
      const { processed = 0, skipped = [], warnings = [] } = res.data || {};
      if (processed > 0) toast.success(`${processed} entrie(s) processed`);
      if (skipped.length > 0) toast.error(`${skipped.length} skipped (${skipped[0]?.reason || 'error'})`);
      if (warnings.length > 0) toast.warning?.(`${warnings.length} over-budget warning(s)`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk action failed');
    }
  };

  const isCurrentMonth = (d) => d.getMonth() === cursor.getMonth();
  const todayIso = iso(new Date());

  return (
    <div className="space-y-4" data-testid="cashflow-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
            <IndianRupee size={22} /> Cash Flow
            {tab !== 'calendar' && (
              <span className="text-sm font-body text-slate-400 font-normal">
                / {tab === 'vendors' ? 'Vendors'
                    : tab === 'reimbursements' ? 'Reimbursements'
                    : tab === 'salaries' ? 'Salaries'
                    : tab === 'consolidated' ? 'Consolidated View'
                    : tab === 'audit' ? 'Audit Log'
                    : 'Admin'}
              </span>
            )}
          </h1>
          <p className="text-sm text-slate-500">
            {tab === 'calendar' && 'Plan expenses on a calendar. Tick when done. Stay within daily limits.'}
            {tab === 'vendors' && 'Shared vendor directory used when tagging payments.'}
            {tab === 'reimbursements' && 'Log expenses you paid personally. Admin marks paid.'}
            {tab === 'salaries' && 'Upload monthly payroll → pay employees in bulk from the calendar.'}
            {tab === 'consolidated' && 'Every manager\u2019s payments in one place \u2014 filter, mark paid, and download.'}
            {tab === 'audit' && 'Full audit trail of every edit, delete, shift and mark-paid action.'}
            {tab === 'admin' && 'Categories, per-manager daily limits & Finance Controller assignments.'}
          </p>
        </div>
      </div>

      {tab === 'calendar' && (
        <>
          {/* Month toolbar */}
          <div className="flex flex-col md:flex-row md:items-center gap-3 bg-white border border-slate-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <button onClick={() => changeMonth(-1)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="cf-prev-month"><ChevronLeft size={18} /></button>
              <div className="text-lg font-heading font-semibold text-slate-800 min-w-[180px] text-center" data-testid="cf-month-label">
                {monthLabel(cursor)}
              </div>
              <button onClick={() => changeMonth(1)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="cf-next-month"><ChevronRight size={18} /></button>
              <button onClick={() => setCursor(new Date())} className="text-xs text-slate-500 hover:text-slate-900 ml-2">Today</button>
            </div>
            {canGlobal && (
              <div className="flex items-center gap-2 md:ml-4">
                <UsersIcon size={14} className="text-slate-500" />
                <select
                  value={selectedManager || ''}
                  onChange={(e) => setSelectedManager(e.target.value)}
                  data-testid="cf-manager-select"
                  className="px-2 py-1.5 text-sm border border-slate-200 rounded bg-white"
                >
                  {managers.map(m => (
                    <option key={m.id} value={m.id}>{m.name} — limit {INR(m.daily_limit || 0)}/day ({m.limit_mode})</option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex-1" />
            <div className="text-xs text-slate-500 flex items-center gap-3">
              <ImportButtons onImported={refresh} />
              <span>Daily limit: <b className="text-slate-800">{INR(effectiveDailyLimit)}</b></span>
              <span className={`px-2 py-0.5 rounded ${effectiveLimitMode === 'hard' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                {effectiveLimitMode === 'hard' ? 'Hard block' : 'Soft warning'}
              </span>
              <button onClick={refresh} className="p-1.5 hover:bg-slate-100 rounded"><RefreshCw size={14} /></button>
            </div>
          </div>

          {/* Search */}
          <div className="bg-white border border-slate-200 rounded-lg p-2 flex items-center gap-2 flex-wrap">
            <Search size={14} className="text-slate-400 ml-1" />
            <input
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search expenses in this month (title, vendor, employee, notes)…"
              data-testid="cf-search"
              className="flex-1 min-w-[240px] px-2 py-1.5 text-sm border-0 focus:outline-none focus:ring-0"
            />
            {searchQ && (
              <>
                <span className="text-[11px] text-slate-500 whitespace-nowrap">
                  <b className="text-slate-800">{searchMatches.entries.length}</b> match(es) across <b className="text-slate-800">{searchMatches.dates.size}</b> day(s)
                </span>
                <button onClick={() => setSearchQ('')} className="p-1 text-slate-400 hover:text-slate-900" data-testid="cf-search-clear">
                  <X size={14} />
                </button>
              </>
            )}
          </div>

          {searchQ && searchMatches.entries.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-64 overflow-y-auto" data-testid="cf-search-results">
              {searchMatches.entries.slice(0, 30).map(m => (
                <button
                  key={m.id}
                  onClick={() => { setOpenDay(m.planned_date); setSearchQ(''); }}
                  data-testid={`cf-search-result-${m.id}`}
                  className="w-full flex items-center gap-3 p-2 hover:bg-slate-50 text-left"
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: m.category_color || '#94a3b8' }} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm truncate ${m.done ? 'line-through text-slate-400' : 'text-slate-800'}`}>{m.title}</div>
                    <div className="text-[11px] text-slate-500">
                      {new Date(m.planned_date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                      {' · '}{m.category_name || '—'}
                      {m.vendor_name && <> · {m.vendor_name}</>}
                      {m.done && m.paid_on && <span className="text-emerald-700"> · paid {m.paid_on}</span>}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-slate-800 flex-shrink-0">{INR(m.amount)}</div>
                </button>
              ))}
              {searchMatches.entries.length > 30 && (
                <div className="p-2 text-center text-[11px] text-slate-500">Showing first 30 of {searchMatches.entries.length}. Refine your search.</div>
              )}
            </div>
          )}

          {/* Calendar grid */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                <div key={d} className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {cells.map((d, idx) => {
                const dIso = iso(d);
                const dayEntries = entriesByDay[dIso] || [];
                const planned = dayEntries.reduce((s, e) => s + (e.amount || 0), 0);
                const doneCnt = dayEntries.filter(e => e.done).length;
                const dim = !isCurrentMonth(d);
                const isToday = dIso === todayIso;
                const eff = effectiveByDay[dIso] || {};
                const dayLimit = eff.effective_limit != null ? eff.effective_limit : effectiveDailyLimit;
                const bonus = eff.bonus || 0;
                const recovery = eff.recovery || 0;
                const overBudget = dayLimit > 0 && planned > dayLimit;
                const matched = searchQ && searchMatches.dates.has(dIso);
                return (
                  <button
                    key={idx}
                    onClick={() => setOpenDay(openDay === dIso ? null : dIso)}
                    data-testid={`cf-day-${dIso}`}
                    className={`text-left border-b border-r border-slate-100 min-h-[92px] p-2 hover:bg-slate-50 transition-colors ${
                      dim ? 'bg-slate-50/50 text-slate-400' : 'bg-white'
                    } ${matched ? 'ring-2 ring-amber-400 ring-inset bg-amber-50' : ''} ${openDay === dIso ? 'ring-2 ring-slate-900 ring-inset z-10' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-semibold ${isToday ? 'bg-slate-900 text-white px-1.5 py-0.5 rounded-full' : ''}`}>{d.getDate()}</span>
                      <div className="flex items-center gap-1">
                        {bonus > 0 && (
                          <span title={`+₹${bonus.toLocaleString('en-IN')} extended`} className="text-[9px] px-1 py-0.5 bg-blue-100 text-blue-700 rounded font-semibold">+{Math.round(bonus/1000)}k</span>
                        )}
                        {recovery > 0 && (
                          <span title={`-₹${recovery.toLocaleString('en-IN')} recovery`} className="text-[9px] px-1 py-0.5 bg-amber-100 text-amber-700 rounded font-semibold">-{Math.round(recovery)}</span>
                        )}
                        {dayEntries.length > 0 && (
                          <span className="text-[10px] text-slate-500">{doneCnt}/{dayEntries.length}</span>
                        )}
                      </div>
                    </div>
                    {dayEntries.length > 0 ? (
                      <div className="mt-2">
                        <div className={`text-sm font-bold ${overBudget ? 'text-red-600' : 'text-slate-800'}`}>
                          {INR(planned)}
                          {dayLimit > 0 && <span className="text-[10px] text-slate-400 font-normal"> / {INR(dayLimit)}</span>}
                        </div>
                        {dayLimit > 0 && (
                          <div className="mt-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${overBudget ? 'bg-red-500' : 'bg-emerald-500'}`}
                              style={{ width: `${Math.min(100, (planned / dayLimit) * 100)}%` }}
                            />
                          </div>
                        )}
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {[...new Set(dayEntries.map(e => e.category_color || '#94a3b8'))].slice(0, 4).map((c, i) => (
                            <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c }} />
                          ))}
                        </div>
                      </div>
                    ) : (!dim && (
                      <div className="mt-3 opacity-0 hover:opacity-100 transition-opacity text-[10px] text-slate-400">
                        {bonus > 0 ? 'Extended day' : 'Click to add'}
                      </div>
                    ))}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected-day expander */}
          {openDay && (
            <DayPanel
              date={openDay}
              entries={entriesByDay[openDay] || []}
              onAdd={() => openCreateForDate(openDay)}
              onExtend={() => { setExtendSeedDate(openDay); setShowExtend(true); }}
              onToggle={toggleDone}
              onDelete={deleteEntry}
              onEdit={(e) => setEditEntry(e)}
              onShift={shiftEntry}
              onBulkAction={bulkAction}
              onClose={() => setOpenDay(null)}
              dailyLimit={(effectiveByDay[openDay]?.effective_limit ?? effectiveDailyLimit)}
              baseLimit={effectiveDailyLimit}
              bonus={effectiveByDay[openDay]?.bonus || 0}
              recovery={effectiveByDay[openDay]?.recovery || 0}
              limitMode={effectiveLimitMode}
              canExtend={!canGlobal || selectedManager === user?.id}
              readOnly={false}
              canGlobal={canGlobal}
            />
          )}

          {loading && <div className="text-center text-xs text-slate-500">Loading…</div>}
        </>
      )}

      {tab === 'admin' && isAdmin && (
        <AdminPanel
          categories={categories}
          setCategories={setCategories}
          managers={managers}
          reloadManagers={() => api.cashflowListManagers().then(r => setManagers(r.data))}
        />
      )}

      {tab === 'vendors' && (
        <VendorsTab
          vendors={vendors}
          reload={() => api.cashflowListVendors().then(r => setVendors(r.data))}
          isAdmin={isAdmin}
        />
      )}

      {tab === 'reimbursements' && (
        <ReimbursementsTab
          isAdmin={isAdmin}
          categories={categories}
          managers={managers}
          currentUserId={user?.id}
        />
      )}

      {tab === 'salaries' && (
        <SalariesTab
          categories={categories}
        />
      )}

      {tab === 'consolidated' && canGlobal && (
        <ConsolidatedTab
          managers={managers}
          categories={categories}
          onEntryChanged={() => {
            // no-op: consolidated tab keeps its own state
          }}
        />
      )}

      {tab === 'audit' && isAdmin && (
        <AuditLogTab managers={managers} />
      )}

      {showCreate && (
        <CreateEntryModal
          seedDate={createSeedDate}
          categories={categories}
          vendors={vendors}
          onClose={() => setShowCreate(false)}
          onSaved={async () => { setShowCreate(false); await refresh(); }}
        />
      )}

      {editEntry && (
        <EditEntryModal
          entry={editEntry}
          categories={categories}
          vendors={vendors}
          onClose={() => setEditEntry(null)}
          onSaved={async () => { setEditEntry(null); await refresh(); }}
        />
      )}

      {showExtend && (
        <ExtendLimitModal
          seedDate={extendSeedDate}
          baseLimit={effectiveDailyLimit}
          onClose={() => setShowExtend(false)}
          onSaved={async () => { setShowExtend(false); await refresh(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Day panel (list of entries for a selected date)
// ─────────────────────────────────────────────────────────────────────────────
function DayPanel({ date, entries, onAdd, onExtend, onToggle, onDelete, onEdit, onShift, onBulkAction, onClose, dailyLimit, baseLimit, bonus, recovery, limitMode, canExtend, readOnly, canGlobal }) {
  const total = entries.reduce((s, e) => s + (e.amount || 0), 0);
  const done = entries.filter(e => e.done).reduce((s, e) => s + (e.amount || 0), 0);
  const over = dailyLimit > 0 && total > dailyLimit;
  const d = new Date(date + 'T00:00:00');

  // Selection state (of unpaid entry ids); reset when the selected day changes
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkShiftDate, setBulkShiftDate] = useState('');
  const [shiftingId, setShiftingId] = useState(null);

  useEffect(() => { setSelectedIds(new Set()); setBulkShiftDate(''); setShiftingId(null); }, [date]);

  // Paid-date popover (FC-only). When set, shows a small date input next to the check.
  const [payingId, setPayingId] = useState(null);
  const [paidOnDraft, setPaidOnDraft] = useState('');
  const [bulkPaidOn, setBulkPaidOn] = useState('');

  const pending = entries.filter(e => !e.done);
  const pendingIds = pending.map(e => e.id);
  const allPendingSelected = pendingIds.length > 0 && pendingIds.every(id => selectedIds.has(id));
  const toggleAll = () => {
    if (allPendingSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(pendingIds));
  };
  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Only include still-pending items in selection
  const activeSelected = Array.from(selectedIds).filter(id => pendingIds.includes(id));
  const selectedTotal = activeSelected.reduce((s, id) => {
    const e = entries.find(x => x.id === id);
    return s + (e ? Number(e.amount || 0) : 0);
  }, 0);

  const doBulk = async (action, extra = {}) => {
    if (!activeSelected.length) return;
    if (action === 'delete' && !window.confirm(`Delete ${activeSelected.length} selected expense(s)?`)) return;
    await onBulkAction({ action, entry_ids: activeSelected, ...extra });
    setSelectedIds(new Set());
    setBulkShiftDate('');
  };

  // Per-entry lock: paid entries are read-only for non-global users
  const isLockedFor = (e) => e.done && !canGlobal;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="cf-day-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-heading text-lg text-slate-900">
            {d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
          </h3>
          <p className="text-xs text-slate-500">
            Planned <b className={over ? 'text-red-600' : 'text-slate-800'}>{INR(total)}</b>
            {dailyLimit > 0 && <> / <b>{INR(dailyLimit)}</b></>}
            {bonus > 0 && <span className="ml-2 inline-flex items-center gap-1 text-blue-700"><TrendingUp size={11} />+{INR(bonus)} extension</span>}
            {recovery > 0 && <span className="ml-2 inline-flex items-center gap-1 text-amber-700">-{INR(recovery)} recovery</span>}
            {' · '}Done <b className="text-emerald-600">{INR(done)}</b>
            {over && <span className="ml-2 inline-flex items-center gap-1 text-red-600"><AlertTriangle size={12} /> over budget</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!readOnly && canExtend && baseLimit > 0 && (
            <button onClick={onExtend} data-testid="cf-extend-limit"
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700">
              <TrendingUp size={14} /> Extend limit
            </button>
          )}
          {!readOnly && (
            <button onClick={onAdd} data-testid="cf-add-entry" className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800">
              <Plus size={14} /> Add expense
            </button>
          )}
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-900"><X size={16} /></button>
        </div>
      </div>

      {/* Bulk action bar (visible when selection > 0) */}
      {activeSelected.length > 0 && (
        <div className="mb-3 p-2 bg-slate-900 text-white rounded-md flex flex-wrap items-center gap-2" data-testid="cf-bulk-bar">
          <span className="text-xs px-2 py-1 bg-white/10 rounded font-semibold" data-testid="cf-bulk-count">
            {activeSelected.length} selected
          </span>
          <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-100 rounded font-semibold" data-testid="cf-bulk-total">
            Total {INR(selectedTotal)}
          </span>
          {canGlobal && (
            <div className="flex items-center gap-1">
              <input
                type="date"
                value={bulkPaidOn}
                max={iso(new Date())}
                onChange={(e) => setBulkPaidOn(e.target.value)}
                placeholder="Paid on"
                data-testid="cf-bulk-paid-on"
                className="px-2 py-1 text-xs text-slate-900 rounded border border-white/20"
              />
              <button
                onClick={() => {
                  if (!bulkPaidOn) return toast.error('Pick payment date');
                  doBulk('mark_done', { paid_on: bulkPaidOn });
                }}
                data-testid="cf-bulk-mark-done"
                className="px-3 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 rounded flex items-center gap-1"
              >
                <Check size={12} /> Mark Paid
              </button>
            </div>
          )}
          <div className="flex items-center gap-1">
            <input
              type="date"
              value={bulkShiftDate}
              onChange={(e) => setBulkShiftDate(e.target.value)}
              data-testid="cf-bulk-shift-date"
              className="px-2 py-1 text-xs text-slate-900 rounded border border-white/20"
            />
            <button
              onClick={() => bulkShiftDate ? doBulk('shift', { new_date: bulkShiftDate }) : toast.error('Pick a date to shift to')}
              disabled={!bulkShiftDate}
              data-testid="cf-bulk-shift"
              className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 flex items-center gap-1"
            >
              <Calendar size={12} /> Shift
            </button>
          </div>
          <button
            onClick={() => doBulk('delete')}
            data-testid="cf-bulk-delete"
            className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 rounded flex items-center gap-1"
          >
            <Trash2 size={12} /> Delete
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="px-2 py-1 text-xs text-white/70 hover:text-white ml-auto"
          >Clear</button>
        </div>
      )}

      {/* Select all header (only when there are pending entries and not read-only) */}
      {!readOnly && pending.length > 0 && (
        <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
          <input
            type="checkbox"
            checked={allPendingSelected}
            onChange={toggleAll}
            data-testid="cf-select-all"
            className="w-4 h-4 rounded border-slate-300"
          />
          <span className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">
            Select all unpaid ({pending.length})
          </span>
        </div>
      )}

      {entries.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400">No expenses planned on this day.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {entries.map(e => {
            const locked = isLockedFor(e);
            const selectable = !readOnly && !e.done;
            const canModify = !readOnly && !locked;
            return (
              <li key={e.id} className="flex items-center gap-3 py-2.5" data-testid={`cf-entry-${e.id}`}>
                {selectable ? (
                  <input
                    type="checkbox"
                    checked={selectedIds.has(e.id)}
                    onChange={() => toggleOne(e.id)}
                    data-testid={`cf-select-${e.id}`}
                    className="w-4 h-4 rounded border-slate-300 flex-shrink-0"
                  />
                ) : (
                  <span className="w-4 flex-shrink-0" />
                )}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    disabled={readOnly || locked || !canGlobal}
                    onClick={() => {
                      if (e.done) {
                        // un-mark paid
                        onToggle(e);
                      } else {
                        // FC — open the paid-date popover
                        setPayingId(payingId === e.id ? null : e.id);
                        setPaidOnDraft(iso(new Date()));
                      }
                    }}
                    data-testid={`cf-toggle-${e.id}`}
                    title={
                      locked ? 'Locked — already marked paid'
                      : !canGlobal ? 'Only Admin or Finance Controller can mark paid'
                      : (e.done ? 'Un-mark paid' : 'Mark paid — enter payment date')
                    }
                    className={`w-5 h-5 flex items-center justify-center rounded border ${
                      e.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 hover:border-emerald-500'
                    } ${(readOnly || locked || !canGlobal) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >{e.done && <Check size={12} />}</button>
                  {payingId === e.id && !e.done && canGlobal && (
                    <div className="flex items-center gap-1 bg-white border border-slate-200 shadow-sm rounded p-1"
                         onClick={(ev) => ev.stopPropagation()}>
                      <input
                        type="date"
                        value={paidOnDraft}
                        max={iso(new Date())}
                        onChange={(ev) => setPaidOnDraft(ev.target.value)}
                        data-testid={`cf-paid-on-${e.id}`}
                        className="px-1.5 py-0.5 text-xs border border-slate-200 rounded"
                      />
                      <button
                        onClick={async () => {
                          if (!paidOnDraft) return toast.error('Pick the payment date');
                          await onToggle(e, paidOnDraft);
                          setPayingId(null);
                          setPaidOnDraft('');
                        }}
                        data-testid={`cf-paid-on-confirm-${e.id}`}
                        className="px-2 py-0.5 text-[11px] bg-emerald-600 text-white rounded hover:bg-emerald-700"
                      >Confirm</button>
                      <button
                        onClick={() => { setPayingId(null); setPaidOnDraft(''); }}
                        className="p-0.5 text-slate-400"
                      ><X size={10} /></button>
                    </div>
                  )}
                </div>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: e.category_color || '#94a3b8' }} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm truncate ${e.done ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                    {e.title}
                    {e.recurrence !== 'none' && (
                      <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-slate-500">
                        <Repeat size={10} /> {e.recurrence}
                      </span>
                    )}
                    {locked && (
                      <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                        Paid · Locked
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {e.category_name}
                    {e.emp_code && <span className="ml-2 inline-flex items-center gap-0.5"><UsersIcon size={9} />{e.emp_code}</span>}
                    {e.bank_name && <span className="ml-2 text-slate-400">{e.bank_name}{e.account_no ? ` · ${e.account_no}` : ''}</span>}
                    {e.vendor_name && <span className="ml-2 inline-flex items-center gap-0.5"><Building2 size={9} />{e.vendor_name}</span>}
                    {e.notes ? ` · ${e.notes}` : ''}
                    {e.done && e.done_by_name && (
                      <span className="ml-2 text-emerald-700">
                        · paid on <b>{e.paid_on || (e.done_at ? new Date(e.done_at).toLocaleDateString('en-IN') : '—')}</b> by {e.done_by_name}
                      </span>
                    )}
                  </div>
                </div>
                <div className={`text-sm font-semibold ${e.done ? 'text-slate-400' : 'text-slate-800'}`}>{INR(e.amount)}</div>
                {canModify && (
                  <>
                    {/* Edit */}
                    <button
                      onClick={() => onEdit(e)}
                      title="Edit expense"
                      data-testid={`cf-edit-${e.id}`}
                      className="p-1 text-slate-400 hover:text-slate-900"
                    >
                      <Pencil size={14} />
                    </button>
                    {/* Shift/reschedule */}
                    <div className="relative">
                      <button
                        onClick={() => setShiftingId(shiftingId === e.id ? null : e.id)}
                        title="Shift to another date"
                        data-testid={`cf-shift-${e.id}`}
                        className="p-1 text-slate-400 hover:text-blue-600"
                      >
                        <Calendar size={14} />
                      </button>
                      {shiftingId === e.id && (
                        <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-slate-200 shadow-lg rounded p-2 flex items-center gap-1"
                             onClick={(ev) => ev.stopPropagation()}>
                          <input
                            type="date"
                            defaultValue={date}
                            data-testid={`cf-shift-date-${e.id}`}
                            id={`shift-date-${e.id}`}
                            className="px-2 py-1 text-xs border border-slate-200 rounded"
                          />
                          <button
                            onClick={async () => {
                              const inp = document.getElementById(`shift-date-${e.id}`);
                              const nd = inp?.value;
                              if (!nd) return toast.error('Pick a date');
                              if (nd === date) { setShiftingId(null); return; }
                              await onShift(e, nd);
                              setShiftingId(null);
                            }}
                            data-testid={`cf-shift-confirm-${e.id}`}
                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                          >Shift</button>
                          <button
                            onClick={() => setShiftingId(null)}
                            className="p-1 text-slate-400 hover:text-slate-900"
                          ><X size={12} /></button>
                        </div>
                      )}
                    </div>
                    {e.recurrence !== 'none' && (
                      <button onClick={() => onDelete(e, true)} title="Delete this + all future" className="p-1 text-slate-400 hover:text-red-600">
                        <Repeat size={14} />
                      </button>
                    )}
                    <button onClick={() => onDelete(e, false)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`cf-delete-${e.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {limitMode === 'hard' && dailyLimit > 0 && (
        <p className="mt-2 text-[11px] text-slate-500">
          <b>Hard-block</b> mode: adding an expense that would exceed the daily limit will be rejected.
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Create entry modal
// ─────────────────────────────────────────────────────────────────────────────
function CreateEntryModal({ seedDate, categories, vendors, onClose, onSaved }) {
  const [entryType, setEntryType] = useState('non_salary');   // 'non_salary' | 'salary'
  // Non-salary fields
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState(categories[0]?.id || '');
  const [vendorId, setVendorId] = useState('');
  // Common
  const [amount, setAmount] = useState('');
  const [plannedDate, setPlannedDate] = useState(seedDate || iso(new Date()));
  const [notes, setNotes] = useState('');
  const [recurrence, setRecurrence] = useState('none');
  // Salary-specific
  const [sal, setSal] = useState({
    emp_code: '', emp_name: '', department: '',
    beneficiary_name: '', bank_name: '', account_no: '', ifsc: '',
  });
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [saving, setSaving] = useState(false);

  const setSalField = (k) => (e) => setSal({ ...sal, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) return toast.error('Amount must be greater than 0');
    if (entryType === 'non_salary') {
      if (!title.trim()) return toast.error('Title required');
      if (!categoryId) return toast.error('Pick a category');
    } else {
      if (!sal.emp_name.trim()) return toast.error('Employee name is required');
    }
    setSaving(true);
    try {
      const payload = entryType === 'salary'
        ? {
            amount: Number(amount),
            planned_date: plannedDate,
            notes: notes.trim() || null,
            recurrence,
            is_salary: true,
            emp_code: sal.emp_code.trim() || null,
            emp_name: sal.emp_name.trim(),
            department: sal.department.trim() || null,
            beneficiary_name: sal.beneficiary_name.trim() || null,
            bank_name: sal.bank_name.trim() || null,
            account_no: sal.account_no.trim() || null,
            ifsc: sal.ifsc.trim() || null,
          }
        : {
            title: title.trim(),
            amount: Number(amount),
            category_id: categoryId,
            vendor_id: vendorId || null,
            planned_date: plannedDate,
            notes: notes.trim() || null,
            recurrence,
          };
      const res = await api.cashflowCreateEntry(payload);
      if (res.data.over_budget_warning) {
        toast.warning('Added, but you are over the daily limit for this day');
      } else if (res.data.created > 1) {
        toast.success(`Added ${res.data.created} recurring entries`);
      } else {
        toast.success(entryType === 'salary' ? 'Salary entry added' : 'Expense added');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const downloadSalarySample = async () => {
    try {
      const res = await api.cashflowSalaryExpenseSampleXlsx();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'salary_expenses_sample.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download sample'); }
  };

  const onImportSalary = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setImporting(true);
    try {
      const res = await api.cashflowSalaryExpenseImport(f);
      setImportReport(res.data);
      const issues = res.data.issue_count ?? res.data.error_count ?? 0;
      const created = res.data.created || 0;
      if (created > 0 && issues === 0) toast.success(`Imported ${created} salary entries`);
      else if (issues > 0) toast.warning?.(`${created} imported · ${issues} row(s) need your review`) || toast.error(`${issues} row(s) need review`);
      else toast.error('No rows imported');
      if (created > 0) onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cf-create-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-heading text-lg">Add Expense</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>

        {/* Type toggle */}
        <div className="px-5 pt-4">
          <div className="flex gap-2 bg-slate-100 p-1 rounded-md" data-testid="cf-type-toggle">
            <button type="button" onClick={() => setEntryType('non_salary')}
                    data-testid="cf-type-non-salary"
                    className={`flex-1 px-3 py-1.5 text-sm rounded ${entryType === 'non_salary' ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-800'}`}>
              Non-salary
            </button>
            <button type="button" onClick={() => setEntryType('salary')}
                    data-testid="cf-type-salary"
                    className={`flex-1 px-3 py-1.5 text-sm rounded ${entryType === 'salary' ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-800'}`}>
              Salary
            </button>
          </div>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          {entryType === 'salary' && (
            <>
              {/* Bulk upload strip */}
              <div className="flex items-center justify-between gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-md">
                <span className="text-xs text-emerald-800">Paying many at once? Bulk-upload an Excel:</span>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={downloadSalarySample} data-testid="cf-sal-sample-btn"
                          className="flex items-center gap-1 px-2 py-1 text-xs border border-slate-200 bg-white rounded hover:bg-slate-100">
                    <Download size={11} /> Sample
                  </button>
                  <label htmlFor="cf-sal-import-inline"
                         className={`flex items-center gap-1 px-2 py-1 text-xs rounded cursor-pointer ${importing ? 'bg-slate-200 text-slate-500' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}>
                    <Upload size={11} /> {importing ? '…' : 'Import Excel'}
                    <input id="cf-sal-import-inline" type="file" accept=".xlsx,.xls" className="sr-only"
                           disabled={importing} onChange={onImportSalary} data-testid="cf-sal-import-inline" />
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Emp Code</label>
                  <input value={sal.emp_code} onChange={setSalField('emp_code')} data-testid="cf-sal-empcode"
                         placeholder="EXAM-1234"
                         className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md font-mono" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Name *</label>
                  <input value={sal.emp_name} onChange={setSalField('emp_name')} data-testid="cf-sal-name" required
                         className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Department</label>
                  <input value={sal.department} onChange={setSalField('department')}
                         className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Beneficiary Name</label>
                  <input value={sal.beneficiary_name} onChange={setSalField('beneficiary_name')}
                         className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Bank Name</label>
                  <input value={sal.bank_name} onChange={setSalField('bank_name')}
                         className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Account No</label>
                  <input value={sal.account_no} onChange={setSalField('account_no')}
                         className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md font-mono" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">IFSC</label>
                  <input value={sal.ifsc} onChange={setSalField('ifsc')}
                         className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md font-mono" />
                </div>
              </div>
            </>
          )}

          {entryType === 'non_salary' && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Title *</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="cf-title"
                       className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
                       placeholder="e.g. Diesel top-up" autoFocus />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Category</label>
                <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} data-testid="cf-category"
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white">
                  <option value="">— pick —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Vendor (optional)</label>
                <select value={vendorId} onChange={(e) => setVendorId(e.target.value)} data-testid="cf-vendor"
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white">
                  <option value="">— none —</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}{v.category ? ` · ${v.category}` : ''}</option>)}
                </select>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Amount (₹) *</label>
              <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cf-amount"
                     className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Date *</label>
              <input type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)}
                     className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">
              {entryType === 'salary' ? 'Remarks' : 'Notes'} (optional)
            </label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Recurring?</label>
            <div className="flex gap-2">
              {['none', 'weekly', 'monthly'].map(r => (
                <button key={r} type="button" onClick={() => setRecurrence(r)}
                        data-testid={`cf-recur-${r}`}
                        className={`flex-1 px-3 py-1.5 text-sm rounded-md border ${
                          recurrence === r ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 text-slate-700'
                        }`}>
                  {r === 'none' ? 'One-off' : r === 'weekly' ? 'Weekly' : 'Monthly'}
                </button>
              ))}
            </div>
            {recurrence !== 'none' && (
              <p className="text-[11px] text-slate-500 mt-1">Creates copies up to 1 year from the start date. You can delete future ones anytime.</p>
            )}
          </div>

          {entryType === 'salary' && (
            <p className="text-[11px] text-slate-500">
              Salary entries auto-file under the <b>Salary</b> category (created on first use).
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
          <button type="submit" disabled={saving} data-testid="cf-save"
                  className="px-4 py-2 text-sm bg-slate-900 text-white rounded-md hover:bg-slate-800 disabled:opacity-50">
            {saving ? 'Saving…' : (entryType === 'salary' ? 'Add salary entry' : 'Add expense')}
          </button>
        </div>

        {importReport && (
          <ImportReportModal
            report={importReport}
            onImported={onSaved}
            onClose={() => setImportReport(null)}
          />
        )}
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin panel — categories + per-manager limits
// ─────────────────────────────────────────────────────────────────────────────
function AdminPanel({ categories, setCategories, managers, reloadManagers }) {
  const [newCat, setNewCat] = useState({ name: '', color: '#0ea5e9' });
  const [savingCat, setSavingCat] = useState(false);

  const addCategory = async (e) => {
    e.preventDefault();
    if (!newCat.name.trim()) return toast.error('Name required');
    setSavingCat(true);
    try {
      const res = await api.cashflowCreateCategory({ name: newCat.name.trim(), color: newCat.color });
      setCategories([...categories, res.data]);
      setNewCat({ name: '', color: '#0ea5e9' });
      toast.success('Category added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSavingCat(false); }
  };
  const removeCategory = async (id) => {
    if (!window.confirm('Delete this category? (only allowed if unused)')) return;
    try {
      await api.cashflowDeleteCategory(id);
      setCategories(categories.filter(c => c.id !== id));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Categories */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <h3 className="font-heading text-base font-semibold mb-3">Expense Categories</h3>
        <form onSubmit={addCategory} className="flex items-center gap-2 mb-3">
          <input type="color" value={newCat.color} onChange={(e) => setNewCat({ ...newCat, color: e.target.value })}
                 className="w-10 h-9 border border-slate-200 rounded" />
          <input value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
                 placeholder="Category name (e.g. Fuel)" data-testid="cf-cat-name"
                 className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-md" />
          <button type="submit" disabled={savingCat} data-testid="cf-cat-add"
                  className="px-3 py-2 text-sm bg-slate-900 text-white rounded-md hover:bg-slate-800">
            <Plus size={14} className="inline -mt-0.5" /> Add
          </button>
        </form>
        <ul className="divide-y divide-slate-100">
          {categories.length === 0 && <li className="py-3 text-sm text-slate-400">No categories yet</li>}
          {categories.map(c => (
            <li key={c.id} className="flex items-center gap-2 py-2" data-testid={`cf-cat-row-${c.id}`}>
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />
              <span className="flex-1 text-sm">{c.name}</span>
              <button onClick={() => removeCategory(c.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      </div>

      {/* Manager limits */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <h3 className="font-heading text-base font-semibold mb-3">Manager Daily Limits</h3>
        <p className="text-xs text-slate-500 mb-3">Users must have the <b>cash_flow</b> module granted via Module Access to appear here.</p>
        <ul className="divide-y divide-slate-100">
          {managers.length === 0 && <li className="py-3 text-sm text-slate-400">Grant the Cash Flow module to a manager first.</li>}
          {managers.map(m => (
            <LimitRow key={m.id} m={m} onSaved={reloadManagers} />
          ))}
        </ul>
      </div>
    </div>
  );
}

function LimitRow({ m, onSaved }) {
  const [limit, setLimit] = useState(m.daily_limit || 0);
  const [mode, setMode] = useState(m.limit_mode || 'soft');
  const [isFC, setIsFC] = useState(!!m.is_finance_controller);
  const [saving, setSaving] = useState(false);
  const [fcSaving, setFcSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.cashflowUpsertLimit(m.id, { daily_limit: Number(limit), limit_mode: mode });
      toast.success(`Limit updated for ${m.name}`);
      onSaved();
    } catch (e) { toast.error('Failed'); }
    finally { setSaving(false); }
  };
  const toggleFC = async () => {
    const next = !isFC;
    setFcSaving(true);
    try {
      await api.cashflowSetFinanceController(m.id, next);
      setIsFC(next);
      toast.success(next ? `${m.name} is now a Finance Controller` : `${m.name} removed as Finance Controller`);
      onSaved();
    } catch (e) { toast.error('Failed to update role'); }
    finally { setFcSaving(false); }
  };
  return (
    <li className="py-2.5 flex items-center gap-2 flex-wrap" data-testid={`cf-limit-${m.id}`}>
      <div className="flex-1 min-w-[140px]">
        <div className="text-sm text-slate-800 flex items-center gap-2">
          {m.name}
          {isFC && (
            <span className="text-[10px] px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded font-semibold uppercase tracking-wide">
              Finance Controller
            </span>
          )}
        </div>
        <div className="text-[11px] text-slate-500">{m.email} · {m.role}</div>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-xs text-slate-500">₹</span>
        <input type="number" min="0" value={limit} onChange={(e) => setLimit(e.target.value)}
               data-testid={`cf-limit-input-${m.id}`}
               className="w-28 px-2 py-1.5 text-sm border border-slate-200 rounded" />
      </div>
      <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid={`cf-mode-${m.id}`}
              className="px-2 py-1.5 text-sm border border-slate-200 rounded bg-white">
        <option value="soft">Soft warn</option>
        <option value="hard">Hard block</option>
      </select>
      <button onClick={save} disabled={saving} className="px-3 py-1.5 text-sm bg-slate-900 text-white rounded hover:bg-slate-800">
        Save
      </button>
      <button
        onClick={toggleFC}
        disabled={fcSaving}
        data-testid={`cf-fc-toggle-${m.id}`}
        title={isFC ? 'Remove Finance Controller role' : 'Grant Finance Controller role (see all managers\u2019 expenses)'}
        className={`px-3 py-1.5 text-xs rounded border transition-colors ${
          isFC
            ? 'bg-violet-600 text-white border-violet-600 hover:bg-violet-700'
            : 'bg-white text-violet-700 border-violet-300 hover:bg-violet-50'
        } disabled:opacity-50`}
      >
        {fcSaving ? '…' : (isFC ? '✓ Finance Controller' : '+ Finance Controller')}
      </button>
    </li>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Edit Entry modal — reuses PUT /entries/{id}. Every save is audit-logged.
// ─────────────────────────────────────────────────────────────────────────────
function EditEntryModal({ entry, categories, vendors, onClose, onSaved }) {
  const isSalary = !!entry.is_salary;
  const [title, setTitle] = useState(entry.title || '');
  const [amount, setAmount] = useState(String(entry.amount ?? ''));
  const [plannedDate, setPlannedDate] = useState((entry.planned_date || '').slice(0, 10));
  const [categoryId, setCategoryId] = useState(entry.category_id || (categories[0]?.id || ''));
  const [vendorId, setVendorId] = useState(entry.vendor_id || '');
  const [notes, setNotes] = useState(entry.notes || '');
  const [sal, setSal] = useState({
    emp_code: entry.emp_code || '',
    emp_name: entry.emp_name || '',
    department: entry.department || '',
    beneficiary_name: entry.beneficiary_name || '',
    bank_name: entry.bank_name || '',
    account_no: entry.account_no || '',
    ifsc: entry.ifsc || '',
  });
  const [saving, setSaving] = useState(false);
  const setSalField = (k) => (e) => setSal({ ...sal, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) return toast.error('Amount must be greater than 0');
    if (!plannedDate) return toast.error('Date required');
    if (isSalary && !sal.emp_name.trim()) return toast.error('Employee name is required');
    if (!isSalary && !title.trim()) return toast.error('Title required');

    setSaving(true);
    try {
      const payload = isSalary
        ? {
            amount: Number(amount),
            planned_date: plannedDate,
            notes: notes.trim() || null,
            emp_code: sal.emp_code.trim() || null,
            emp_name: sal.emp_name.trim(),
            department: sal.department.trim() || null,
            beneficiary_name: sal.beneficiary_name.trim() || null,
            bank_name: sal.bank_name.trim() || null,
            account_no: sal.account_no.trim() || null,
            ifsc: sal.ifsc.trim() || null,
            title: `Salary — ${sal.emp_name.trim()}${sal.emp_code.trim() ? ` (${sal.emp_code.trim()})` : ''}`,
          }
        : {
            title: title.trim(),
            amount: Number(amount),
            category_id: categoryId,
            vendor_id: vendorId || null,
            planned_date: plannedDate,
            notes: notes.trim() || null,
          };
      const res = await api.cashflowUpdateEntry(entry.id, payload);
      if (res.data.over_budget_warning) {
        toast.warning?.('Saved, but this day is now over the daily limit') || toast.success('Saved');
      } else {
        toast.success('Expense updated');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cf-edit-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-heading text-lg">Edit Expense</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          {isSalary ? (
            <>
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Employee name *" value={sal.emp_name} onChange={setSalField('emp_name')}
                       data-testid="cf-edit-emp-name" className="px-3 py-2 text-sm border border-slate-200 rounded col-span-2" />
                <input placeholder="Emp code" value={sal.emp_code} onChange={setSalField('emp_code')}
                       className="px-3 py-2 text-sm border border-slate-200 rounded" />
                <input placeholder="Department" value={sal.department} onChange={setSalField('department')}
                       className="px-3 py-2 text-sm border border-slate-200 rounded" />
                <input placeholder="Beneficiary" value={sal.beneficiary_name} onChange={setSalField('beneficiary_name')}
                       className="px-3 py-2 text-sm border border-slate-200 rounded col-span-2" />
                <input placeholder="Bank" value={sal.bank_name} onChange={setSalField('bank_name')}
                       className="px-3 py-2 text-sm border border-slate-200 rounded" />
                <input placeholder="Account No" value={sal.account_no} onChange={setSalField('account_no')}
                       className="px-3 py-2 text-sm border border-slate-200 rounded" />
                <input placeholder="IFSC" value={sal.ifsc} onChange={setSalField('ifsc')}
                       className="px-3 py-2 text-sm border border-slate-200 rounded col-span-2" />
              </div>
            </>
          ) : (
            <>
              <input placeholder="Title *" value={title} onChange={(e) => setTitle(e.target.value)}
                     data-testid="cf-edit-title" className="w-full px-3 py-2 text-sm border border-slate-200 rounded" />
              <div className="grid grid-cols-2 gap-2">
                <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
                        className="px-3 py-2 text-sm border border-slate-200 rounded bg-white">
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}
                        className="px-3 py-2 text-sm border border-slate-200 rounded bg-white">
                  <option value="">No vendor</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-2">
            <input type="number" min="0" placeholder="Amount *" value={amount} onChange={(e) => setAmount(e.target.value)}
                   data-testid="cf-edit-amount" className="px-3 py-2 text-sm border border-slate-200 rounded" />
            <input type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)}
                   data-testid="cf-edit-date" className="px-3 py-2 text-sm border border-slate-200 rounded" />
          </div>
          <textarea placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded" rows={2} />
          <p className="text-[11px] text-slate-500">
            Every edit is recorded in the audit log (visible to admin).
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm bg-white text-slate-700 border border-slate-300 rounded hover:bg-slate-100">Cancel</button>
          <button type="submit" disabled={saving} data-testid="cf-edit-save"
                  className="px-4 py-2 text-sm bg-slate-900 text-white rounded hover:bg-slate-800 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Extend Limit modal
// ─────────────────────────────────────────────────────────────────────────────
function ExtendLimitModal({ seedDate, baseLimit, onClose, onSaved }) {
  const [targetDate, setTargetDate] = useState(seedDate || iso(new Date()));
  const [extraAmount, setExtraAmount] = useState('');
  const [recoverOver, setRecoverOver] = useState(7);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const perDay = extraAmount && recoverOver > 0
    ? (Number(extraAmount) / Number(recoverOver))
    : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!extraAmount || Number(extraAmount) <= 0) return toast.error('Extension amount > 0');
    if (!recoverOver || Number(recoverOver) < 1) return toast.error('Recover over at least 1 day');
    setSaving(true);
    try {
      await api.cashflowExtendLimit({
        target_date: targetDate,
        extra_amount: Number(extraAmount),
        recover_over_days: Number(recoverOver),
        notes: notes.trim() || null,
      });
      toast.success(`Limit extended by ${INR(extraAmount)}`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cf-extend-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-heading text-lg flex items-center gap-2"><TrendingUp size={18} /> Extend Daily Limit</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-md p-2">
            Base limit is <b>{INR(baseLimit)}</b>. Extension adds to this day&apos;s limit and is recovered by
            reducing the next few days&apos; limits proportionally.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Date</label>
              <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)}
                     className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Extra ₹</label>
              <input type="number" min="1" value={extraAmount} onChange={(e) => setExtraAmount(e.target.value)}
                     data-testid="cf-ext-amount"
                     className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Recover over next N days</label>
            <input type="number" min="1" max="90" value={recoverOver} onChange={(e) => setRecoverOver(e.target.value)}
                   data-testid="cf-ext-days"
                   className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md" />
            {perDay > 0 && (
              <p className="text-[11px] text-slate-500 mt-1">
                Next {recoverOver} days will each lose <b>{INR(perDay.toFixed(2))}</b> from their limit
                (new effective limit will be <b>{INR(Math.max(0, baseLimit - perDay).toFixed(2))}</b>).
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Notes (optional)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Why did you need extra today?"
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
          <button type="submit" disabled={saving} data-testid="cf-ext-save"
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Extend limit'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Vendors tab
// ─────────────────────────────────────────────────────────────────────────────
function VendorsTab({ vendors, reload, isAdmin }) {
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = vendors.filter(v =>
    !search
    || v.name?.toLowerCase().includes(search.toLowerCase())
    || (v.phone || '').includes(search)
    || (v.category || '').toLowerCase().includes(search.toLowerCase())
  );

  const remove = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try {
      await api.cashflowDeleteVendor(id);
      toast.success('Vendor deleted');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="cf-vendors-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="font-heading text-base font-semibold">Vendor Directory</h3>
        <span className="text-xs text-slate-500">Shared across all managers · used when tagging payments</span>
        <div className="flex-1" />
        <input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Search vendor…" data-testid="cf-vendor-search"
               className="px-3 py-1.5 text-sm border border-slate-200 rounded-md w-56" />
        <button onClick={() => setShowCreate(true)} data-testid="cf-vendor-add"
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-900 text-white rounded-md hover:bg-slate-800">
          <Plus size={14} /> Add vendor
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-slate-400">No vendors yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-2 px-2">Name</th>
                <th className="text-left py-2 px-2">Phone</th>
                <th className="text-left py-2 px-2">Category</th>
                <th className="text-left py-2 px-2">GST</th>
                <th className="text-left py-2 px-2">Bank · IFSC</th>
                <th className="text-right py-2 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(v => (
                <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cf-vendor-row-${v.id}`}>
                  <td className="py-2 px-2 font-medium text-slate-800">{v.name}</td>
                  <td className="py-2 px-2 text-slate-600">{v.phone || '—'}</td>
                  <td className="py-2 px-2 text-slate-600">{v.category || '—'}</td>
                  <td className="py-2 px-2 text-slate-600 font-mono text-xs">{v.gst || '—'}</td>
                  <td className="py-2 px-2 text-slate-600 font-mono text-xs">
                    {v.bank_account ? `${v.bank_account} · ${v.ifsc || '?'}` : '—'}
                  </td>
                  <td className="py-2 px-2 text-right">
                    <button onClick={() => setEditing(v)} className="p-1 text-slate-500 hover:text-slate-900" title="Edit">
                      <Settings size={14} />
                    </button>
                    {isAdmin && (
                      <button onClick={() => remove(v.id)} className="p-1 text-slate-400 hover:text-red-600" title="Delete">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(showCreate || editing) && (
        <VendorModal
          vendor={editing}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function VendorModal({ vendor, onClose, onSaved }) {
  const [f, setF] = useState({
    name: vendor?.name || '', phone: vendor?.phone || '', email: vendor?.email || '',
    address: vendor?.address || '', gst: vendor?.gst || '',
    bank_account: vendor?.bank_account || '', ifsc: vendor?.ifsc || '',
    category: vendor?.category || '', notes: vendor?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!vendor;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      if (isEdit) await api.cashflowUpdateVendor(vendor.id, f);
      else await api.cashflowCreateVendor(f);
      toast.success(isEdit ? 'Vendor updated' : 'Vendor added');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cf-vendor-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="font-heading text-lg">{isEdit ? 'Edit Vendor' : 'Add Vendor'}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="Name *" v={f.name} on={set('name')} testId="cf-v-name" required />
          <Field label="Phone" v={f.phone} on={set('phone')} />
          <Field label="Email" v={f.email} on={set('email')} type="email" />
          <Field label="Category" v={f.category} on={set('category')} placeholder="e.g. Fuel, Logistics" />
          <Field label="GST Number" v={f.gst} on={set('gst')} className="col-span-2" mono />
          <Field label="Bank Account" v={f.bank_account} on={set('bank_account')} mono />
          <Field label="IFSC" v={f.ifsc} on={set('ifsc')} mono />
          <Field label="Address" v={f.address} on={set('address')} className="col-span-2" />
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Notes</label>
            <textarea value={f.notes} onChange={set('notes')} rows={2}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 sticky bottom-0">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
          <button type="submit" disabled={saving} data-testid="cf-v-save"
                  className="px-4 py-2 text-sm bg-slate-900 text-white rounded-md hover:bg-slate-800 disabled:opacity-50">
            {saving ? 'Saving…' : (isEdit ? 'Update' : 'Add vendor')}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, v, on, type = 'text', placeholder, className = '', mono = false, required = false, testId }) {
  return (
    <div className={className}>
      <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">{label}</label>
      <input type={type} value={v} onChange={on} placeholder={placeholder} required={required}
             data-testid={testId}
             className={`w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 ${mono ? 'font-mono' : ''}`} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reimbursements tab
// ─────────────────────────────────────────────────────────────────────────────
function ReimbursementsTab({ isAdmin, categories, managers, currentUserId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (isAdmin && managerFilter) params.manager_id = managerFilter;
      const r = await api.cashflowListReimbursements(params);
      setRows(r.data || []);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, [statusFilter, managerFilter]);

  const totals = rows.reduce((acc, r) => {
    acc.total += r.amount;
    if (r.status === 'paid') acc.paid += r.amount;
    else acc.logged += r.amount;
    return acc;
  }, { total: 0, paid: 0, logged: 0 });

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try { await api.cashflowDeleteReimbursement(r.id); toast.success('Deleted'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const markPaid = async (r) => {
    try { await api.cashflowMarkReimbursementPaid(r.id); toast.success('Marked paid'); reload(); }
    catch (e) { toast.error('Failed'); }
  };
  const unmarkPaid = async (r) => {
    if (!window.confirm('Revert to logged?')) return;
    try { await api.cashflowUnmarkReimbursementPaid(r.id); toast.success('Reverted'); reload(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-3" data-testid="cf-reimb-tab">
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Total requested" value={INR(totals.total)} tone="slate" />
        <StatCard label="Pending payment" value={INR(totals.logged)} tone="amber" />
        <StatCard label="Paid out" value={INR(totals.paid)} tone="emerald" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <h3 className="font-heading text-base font-semibold">Reimbursements</h3>
          <div className="flex-1" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-200 rounded-md bg-white">
            <option value="">All statuses</option>
            <option value="logged">Pending</option>
            <option value="paid">Paid</option>
          </select>
          {isAdmin && (
            <select value={managerFilter} onChange={(e) => setManagerFilter(e.target.value)}
                    className="px-2 py-1.5 text-sm border border-slate-200 rounded-md bg-white">
              <option value="">All managers</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          )}
          <button onClick={() => setShowCreate(true)} data-testid="cf-reimb-add"
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-900 text-white rounded-md hover:bg-slate-800">
            <Plus size={14} /> Log expense
          </button>
        </div>

        {loading ? <div className="text-center text-sm text-slate-400 py-6">Loading…</div>
          : rows.length === 0 ? <div className="text-center text-sm text-slate-400 py-12">No reimbursements yet.</div>
          : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-left py-2 px-2">Title / Nature</th>
                  {isAdmin && <th className="text-left py-2 px-2">Manager</th>}
                  <th className="text-left py-2 px-2">Category</th>
                  <th className="text-right py-2 px-2">Amount</th>
                  <th className="text-center py-2 px-2">Invoice</th>
                  <th className="text-center py-2 px-2">Status</th>
                  <th className="text-right py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const canEdit = (isAdmin || r.manager_id === currentUserId) && r.status !== 'paid';
                  return (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cf-reimb-row-${r.id}`}>
                      <td className="py-2 px-2 text-slate-600">{r.date_incurred}</td>
                      <td className="py-2 px-2">
                        <div className="font-medium text-slate-800">{r.title}</div>
                        <div className="text-[11px] text-slate-500">{r.nature}</div>
                      </td>
                      {isAdmin && <td className="py-2 px-2 text-slate-600">{r.manager_name}</td>}
                      <td className="py-2 px-2 text-slate-600">
                        {r.category_name && <><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: r.category_color }} />{r.category_name}</>}
                      </td>
                      <td className="py-2 px-2 text-right font-semibold">{INR(r.amount)}</td>
                      <td className="py-2 px-2 text-center">
                        <InvoiceCell r={r} canEdit={canEdit || isAdmin} onSaved={reload} />
                      </td>
                      <td className="py-2 px-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs ${r.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {r.status === 'paid' ? 'Paid' : 'Pending'}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        {isAdmin && r.status !== 'paid' && (
                          <button onClick={() => markPaid(r)} data-testid={`cf-reimb-pay-${r.id}`}
                                  className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 mr-1">
                            Mark paid
                          </button>
                        )}
                        {isAdmin && r.status === 'paid' && (
                          <button onClick={() => unmarkPaid(r)}
                                  className="px-2 py-1 text-xs bg-slate-200 text-slate-700 rounded hover:bg-slate-300 mr-1">
                            Revert
                          </button>
                        )}
                        {(isAdmin || (r.manager_id === currentUserId && r.status !== 'paid')) && (
                          <button onClick={() => remove(r)} className="p-1 text-slate-400 hover:text-red-600">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <ReimbursementModal
          categories={categories}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); reload(); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const toneMap = {
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  };
  return (
    <div className={`border rounded-lg p-3 ${toneMap[tone] || toneMap.slate}`}>
      <div className="text-[10px] uppercase font-semibold tracking-wide opacity-80">{label}</div>
      <div className="text-xl font-bold mt-1">{value}</div>
    </div>
  );
}

function InvoiceCell({ r, canEdit, onSaved }) {
  const inputId = `invoice-input-${r.id}`;
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      await api.cashflowUploadInvoice(r.id, f);
      toast.success('Invoice uploaded');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
  };
  if (r.invoice_url) {
    return (
      <a href={r.invoice_url} target="_blank" rel="noreferrer" title={r.invoice_filename || 'View invoice'}
         className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs">
        <FileText size={14} /> View <ExternalLink size={10} />
      </a>
    );
  }
  if (!canEdit) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <label htmlFor={inputId} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 cursor-pointer">
      <Upload size={14} /> Upload
      <input type="file" id={inputId} onChange={onFile}
             accept="image/*,application/pdf"
             data-testid={`cf-reimb-upload-${r.id}`}
             className="sr-only" />
    </label>
  );
}

function ReimbursementModal({ categories, onClose, onSaved }) {
  const [f, setF] = useState({
    title: '', nature: '', amount: '',
    date_incurred: iso(new Date()),
    category_id: '', notes: '',
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error('Title required');
    if (!f.nature.trim()) return toast.error('Nature of expense required');
    if (!f.amount || Number(f.amount) <= 0) return toast.error('Amount > 0');
    setSaving(true);
    try {
      const created = await api.cashflowCreateReimbursement({
        title: f.title.trim(),
        nature: f.nature.trim(),
        amount: Number(f.amount),
        date_incurred: f.date_incurred,
        category_id: f.category_id || null,
        notes: f.notes.trim() || null,
      });
      if (file) {
        try {
          await api.cashflowUploadInvoice(created.data.id, file);
        } catch (e) { toast.warning('Reimbursement saved but invoice upload failed'); }
      }
      toast.success('Reimbursement logged');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cf-reimb-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="font-heading text-lg flex items-center gap-2"><Receipt size={18} /> Log Reimbursement</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Title *" v={f.title} on={set('title')} placeholder="e.g. Cab from airport" testId="cf-reimb-title" required />
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Nature of expense *</label>
            <textarea value={f.nature} onChange={set('nature')} rows={2} required
                      data-testid="cf-reimb-nature"
                      placeholder="Short description e.g. Client meeting travel"
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount ₹ *" v={f.amount} on={set('amount')} type="number" testId="cf-reimb-amount" required />
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Date</label>
              <input type="date" value={f.date_incurred} onChange={set('date_incurred')}
                     className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Category</label>
            <select value={f.category_id} onChange={set('category_id')}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white">
              <option value="">— none —</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Invoice / Challan (optional)</label>
            <input type="file" accept="image/*,application/pdf"
                   onChange={(e) => setFile(e.target.files?.[0] || null)}
                   data-testid="cf-reimb-file"
                   className="w-full text-sm text-slate-700" />
            {file && <p className="text-[11px] text-slate-500 mt-1">Selected: {file.name} ({Math.round(file.size / 1024)} KB)</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Notes</label>
            <textarea value={f.notes} onChange={set('notes')} rows={2}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 sticky bottom-0">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
          <button type="submit" disabled={saving} data-testid="cf-reimb-save"
                  className="px-4 py-2 text-sm bg-slate-900 text-white rounded-md hover:bg-slate-800 disabled:opacity-50">
            {saving ? 'Saving…' : 'Log reimbursement'}
          </button>
        </div>
      </form>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Excel bulk-import buttons (Sample + Import)
// ─────────────────────────────────────────────────────────────────────────────
function ImportButtons({ onImported }) {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const inputId = 'cf-bulk-import-input';

  const downloadSample = async () => {
    try {
      const res = await api.cashflowSampleXlsx();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'cashflow_sample.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download sample'); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // allow re-upload of same file
    if (!f) return;
    setBusy(true);
    try {
      const res = await api.cashflowImportXlsx(f);
      setReport(res.data);
      const errs = res.data.error_count || 0;
      if (res.data.created > 0 && errs === 0) toast.success(`Imported ${res.data.created} entries`);
      else if (res.data.created > 0 && errs > 0) toast.warning(`Imported ${res.data.created}, skipped ${errs}`);
      else toast.error(`No rows imported (${errs} errors)`);
      await onImported();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={downloadSample} data-testid="cf-download-sample"
              className="flex items-center gap-1 px-2 py-1 border border-slate-200 rounded-md text-slate-700 hover:bg-slate-100">
        <Download size={12} /> Sample
      </button>
      <label htmlFor={inputId} data-testid="cf-import-label"
             className={`flex items-center gap-1 px-2 py-1 border rounded-md cursor-pointer ${busy ? 'opacity-50 bg-slate-100' : 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'}`}>
        <Upload size={12} /> {busy ? 'Importing…' : 'Import Excel'}
        <input id={inputId} type="file" accept=".xlsx,.xls" onChange={onFile}
               data-testid="cf-import-file" disabled={busy} className="sr-only" />
      </label>
      {report && (
        <ImportReportModal report={report} onClose={() => setReport(null)} />
      )}
    </>
  );
}

function ImportReportModal({ report, onClose, onImported }) {
  // Rows the backend flagged (may include name-empty, invalid amount/date, over-budget).
  // Manager can either "Skip" (dismiss) or "Fix & keep" (edit inline → POST /entries).
  const initial = (report.issues || report.errors || []).map((e, idx) => ({
    key: `${e.row}-${idx}`,
    row: e.row,
    reason: e.reason,
    raw: e.raw || {},
    editing: false,
    // draft form values seeded from raw
    draft: {
      emp_name: (e.raw?.emp_name || '').trim(),
      emp_code: (e.raw?.emp_code || '').trim(),
      amount: (e.raw?.amount || '').toString().trim(),
      planned_date: (e.raw?.planned_date || iso(new Date())).toString().slice(0, 10),
      department: (e.raw?.department || '').trim(),
      beneficiary_name: (e.raw?.beneficiary_name || '').trim(),
      bank_name: (e.raw?.bank_name || '').trim(),
      account_no: (e.raw?.account_no || '').trim(),
      ifsc: (e.raw?.ifsc || '').trim(),
      remarks: (e.raw?.remarks || '').trim(),
    },
    status: 'pending', // pending | skipped | added
    busy: false,
  }));
  const [items, setItems] = useState(initial);

  const setItem = (key, patch) =>
    setItems(prev => prev.map(it => it.key === key ? { ...it, ...patch } : it));
  const setDraft = (key, patch) =>
    setItems(prev => prev.map(it => it.key === key ? { ...it, draft: { ...it.draft, ...patch } } : it));

  const pending = items.filter(x => x.status === 'pending').length;
  const added = items.filter(x => x.status === 'added').length;
  const skipped = items.filter(x => x.status === 'skipped').length;

  const skip = (key) => setItem(key, { status: 'skipped', editing: false });
  const edit = (key) => setItem(key, { editing: true });
  const cancelEdit = (key) => setItem(key, { editing: false });

  const saveOne = async (item) => {
    const d = item.draft;
    if (!d.emp_name.trim()) return toast.error('Name is required');
    const amt = Number(d.amount);
    if (!amt || amt <= 0) return toast.error('Amount must be > 0');
    if (!d.planned_date) return toast.error('Date is required');
    setItem(item.key, { busy: true });
    try {
      const title = `Salary — ${d.emp_name.trim()}${d.emp_code ? ` (${d.emp_code.trim()})` : ''}`;
      await api.cashflowCreateEntry({
        title,
        amount: amt,
        planned_date: d.planned_date,
        notes: d.remarks || null,
        is_salary: true,
        emp_name: d.emp_name.trim(),
        emp_code: d.emp_code.trim() || null,
        department: d.department.trim() || null,
        beneficiary_name: d.beneficiary_name.trim() || null,
        bank_name: d.bank_name.trim() || null,
        account_no: d.account_no.trim() || null,
        ifsc: d.ifsc.trim() || null,
      });
      setItem(item.key, { status: 'added', editing: false, busy: false });
      toast.success(`Row ${item.row} added`);
      onImported && onImported();
    } catch (err) {
      setItem(item.key, { busy: false });
      toast.error(err?.response?.data?.detail || 'Failed to add row');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cf-import-report">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <h3 className="font-heading text-lg">Import result</h3>
            <p className="text-xs text-slate-500">Review the rows that were skipped — fix or dismiss each.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
              <div className="text-2xl font-bold text-emerald-700">{(report.created || 0) + added}</div>
              <div className="text-[10px] uppercase text-emerald-700 tracking-wide">Created</div>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded">
              <div className="text-2xl font-bold text-amber-700">{report.over_budget_warnings || 0}</div>
              <div className="text-[10px] uppercase text-amber-700 tracking-wide">Over budget</div>
            </div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded">
              <div className="text-2xl font-bold text-slate-700">{pending}</div>
              <div className="text-[10px] uppercase text-slate-700 tracking-wide">Needs review</div>
            </div>
            <div className="p-3 bg-red-50 border border-red-200 rounded">
              <div className="text-2xl font-bold text-red-700">{skipped}</div>
              <div className="text-[10px] uppercase text-red-700 tracking-wide">Skipped</div>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="text-center text-sm text-slate-400 py-6">All rows imported cleanly.</div>
          ) : (
            <div>
              <h4 className="text-xs uppercase font-semibold text-slate-600 mb-2">Rows to decide on</h4>
              <ul className="space-y-2">
                {items.map(it => (
                  <li key={it.key}
                      data-testid={`cf-issue-${it.row}`}
                      className={`border rounded-md p-3 ${
                        it.status === 'added' ? 'bg-emerald-50 border-emerald-200'
                        : it.status === 'skipped' ? 'bg-slate-50 border-slate-200 opacity-60'
                        : 'bg-red-50 border-red-200'
                      }`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-mono text-slate-500">Excel row {it.row}</div>
                        <div className="text-sm text-slate-800 mt-0.5">
                          {it.status === 'added' && <span className="text-emerald-700 font-semibold">✓ Added</span>}
                          {it.status === 'skipped' && <span className="text-slate-500 font-semibold">Skipped</span>}
                          {it.status === 'pending' && <span className="text-red-700 font-semibold">{it.reason}</span>}
                        </div>
                        {it.status === 'pending' && !it.editing && (
                          <div className="text-[11px] text-slate-500 mt-1 font-mono truncate">
                            {[it.raw.emp_name, it.raw.emp_code, it.raw.amount, it.raw.planned_date]
                              .filter(Boolean).join(' · ') || '(empty row)'}
                          </div>
                        )}
                      </div>
                      {it.status === 'pending' && !it.editing && (
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={() => edit(it.key)}
                            data-testid={`cf-issue-fix-${it.row}`}
                            className="px-2.5 py-1 text-xs bg-slate-900 text-white rounded hover:bg-slate-800"
                          >Fix & keep</button>
                          <button
                            onClick={() => skip(it.key)}
                            data-testid={`cf-issue-skip-${it.row}`}
                            className="px-2.5 py-1 text-xs bg-white text-slate-600 border border-slate-300 rounded hover:bg-slate-100"
                          >Skip</button>
                        </div>
                      )}
                    </div>

                    {/* Inline edit form */}
                    {it.editing && (
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <input placeholder="Employee name *" value={it.draft.emp_name}
                               data-testid={`cf-fix-emp-name-${it.row}`}
                               onChange={(e) => setDraft(it.key, { emp_name: e.target.value })}
                               className="px-2 py-1.5 text-sm border border-slate-200 rounded col-span-2" />
                        <input placeholder="Emp code" value={it.draft.emp_code}
                               onChange={(e) => setDraft(it.key, { emp_code: e.target.value })}
                               className="px-2 py-1.5 text-sm border border-slate-200 rounded" />
                        <input type="number" placeholder="Amount *" value={it.draft.amount}
                               data-testid={`cf-fix-amount-${it.row}`}
                               onChange={(e) => setDraft(it.key, { amount: e.target.value })}
                               className="px-2 py-1.5 text-sm border border-slate-200 rounded" />
                        <input type="date" value={it.draft.planned_date}
                               data-testid={`cf-fix-date-${it.row}`}
                               onChange={(e) => setDraft(it.key, { planned_date: e.target.value })}
                               className="px-2 py-1.5 text-sm border border-slate-200 rounded" />
                        <input placeholder="Department" value={it.draft.department}
                               onChange={(e) => setDraft(it.key, { department: e.target.value })}
                               className="px-2 py-1.5 text-sm border border-slate-200 rounded" />
                        <input placeholder="Beneficiary" value={it.draft.beneficiary_name}
                               onChange={(e) => setDraft(it.key, { beneficiary_name: e.target.value })}
                               className="px-2 py-1.5 text-sm border border-slate-200 rounded" />
                        <input placeholder="Bank" value={it.draft.bank_name}
                               onChange={(e) => setDraft(it.key, { bank_name: e.target.value })}
                               className="px-2 py-1.5 text-sm border border-slate-200 rounded" />
                        <input placeholder="Account No" value={it.draft.account_no}
                               onChange={(e) => setDraft(it.key, { account_no: e.target.value })}
                               className="px-2 py-1.5 text-sm border border-slate-200 rounded" />
                        <input placeholder="IFSC" value={it.draft.ifsc}
                               onChange={(e) => setDraft(it.key, { ifsc: e.target.value })}
                               className="px-2 py-1.5 text-sm border border-slate-200 rounded" />
                        <input placeholder="Remarks" value={it.draft.remarks}
                               onChange={(e) => setDraft(it.key, { remarks: e.target.value })}
                               className="px-2 py-1.5 text-sm border border-slate-200 rounded col-span-2" />
                        <div className="col-span-2 flex justify-end gap-2 mt-1">
                          <button
                            onClick={() => cancelEdit(it.key)}
                            className="px-3 py-1.5 text-xs bg-white text-slate-600 border border-slate-300 rounded hover:bg-slate-50"
                          >Cancel</button>
                          <button
                            onClick={() => saveOne(it)}
                            disabled={it.busy}
                            data-testid={`cf-fix-save-${it.row}`}
                            className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                          >{it.busy ? 'Adding…' : 'Add to Cash Flow'}</button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              {pending > 0 && (
                <p className="text-[11px] text-slate-500 mt-2">
                  You have <b>{pending}</b> row(s) still to review. Fix them here or skip to leave out.
                </p>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-slate-900 text-white rounded-md hover:bg-slate-800">Done</button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Salaries tab — monthly payroll + bulk pay from calendar
// ─────────────────────────────────────────────────────────────────────────────
function SalariesTab({ categories }) {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [months, setMonths] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({});
  const [showPay, setShowPay] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.cashflowListSalaries({ month }),
        api.cashflowSalarySummary(month),
      ]);
      setRows(r.data || []);
      setSummary(s.data);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    api.cashflowSalaryMonths().then(r => setMonths(r.data)).catch(() => {});
  }, []);
  useEffect(() => { reload(); setSelected({}); }, [month]);

  const filtered = rows.filter(r =>
    !search ||
    r.employee_id?.toLowerCase().includes(search.toLowerCase()) ||
    r.name?.toLowerCase().includes(search.toLowerCase())
  );

  const selectedItems = filtered.filter(r => selected[r.employee_id]?.checked);
  const selectedTotal = selectedItems.reduce(
    (s, r) => s + (Number(selected[r.employee_id]?.amount) || 0), 0
  );

  const toggleAll = (checked) => {
    const next = { ...selected };
    filtered.forEach(r => {
      const pending = r.pending || 0;
      if (checked && pending <= 0.01) return;
      next[r.employee_id] = { checked, amount: checked ? (next[r.employee_id]?.amount ?? pending) : 0 };
    });
    setSelected(next);
  };
  const toggleRow = (r) => {
    setSelected(prev => {
      const cur = prev[r.employee_id] || {};
      const checked = !cur.checked;
      return { ...prev, [r.employee_id]: { checked, amount: checked ? (cur.amount ?? r.pending) : cur.amount } };
    });
  };
  const setAmount = (empId, val) => {
    setSelected(prev => ({ ...prev, [empId]: { ...(prev[empId] || {}), amount: val } }));
  };

  const downloadSample = async () => {
    try {
      const res = await api.cashflowSalarySampleXlsx();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'salaries_sample.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download sample'); }
  };
  const onImport = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setImporting(true);
    try {
      const res = await api.cashflowImportSalaries(f, month);
      setImportReport(res.data);
      toast.success(
        `Imported ${res.data.salary_rows} rows · ${res.data.new_employees} new, ${res.data.updated_employees} updated`
      );
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="cf-salaries-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Employees" value={summary?.employees ?? 0} tone="slate" />
        <StatCard label="Total payable" value={INR(summary?.total_net || 0)} tone="slate" />
        <StatCard label="Paid" value={INR(summary?.total_paid || 0)} tone="emerald" />
        <StatCard label="Pending" value={INR(summary?.total_pending || 0)} tone="amber" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-2">
        <label className="text-xs font-semibold text-slate-600 uppercase">Month</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
               data-testid="cf-sal-month"
               className="px-2 py-1.5 text-sm border border-slate-200 rounded" />
        {months.length > 0 && (
          <select onChange={(e) => e.target.value && setMonth(e.target.value)} value=""
                  className="px-2 py-1.5 text-sm border border-slate-200 rounded bg-white">
            <option value="">Recent months…</option>
            {months.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
        <input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Search by E-Code or name…" data-testid="cf-sal-search"
               className="flex-1 min-w-[180px] px-3 py-1.5 text-sm border border-slate-200 rounded" />
        <button onClick={downloadSample} data-testid="cf-sal-sample"
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-slate-200 rounded hover:bg-slate-100">
          <Download size={12} /> Sample
        </button>
        <label htmlFor="cf-sal-import-file"
               className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded cursor-pointer ${importing ? 'bg-slate-100 text-slate-400' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}>
          <Upload size={12} /> {importing ? 'Importing…' : `Import ${month}`}
          <input id="cf-sal-import-file" type="file" accept=".xlsx,.xls" className="sr-only"
                 disabled={importing} onChange={onImport} data-testid="cf-sal-import-file" />
        </label>
      </div>

      {selectedItems.length > 0 && (
        <div className="sticky top-2 z-10 bg-slate-900 text-white rounded-lg p-3 flex items-center gap-3 shadow-lg" data-testid="cf-sal-selbar">
          <span className="text-sm">{selectedItems.length} selected · <b>{INR(selectedTotal)}</b> total</span>
          <div className="flex-1" />
          <button onClick={() => setSelected({})} className="text-xs text-slate-300 hover:text-white">Clear</button>
          <button onClick={() => setShowPay(true)} data-testid="cf-sal-pay-btn"
                  className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500 text-white rounded hover:bg-emerald-600">
            <Plus size={14} /> Add to Cash Flow
          </button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? <div className="text-center text-sm text-slate-400 py-8">Loading…</div>
          : filtered.length === 0 ? (
            <div className="text-center text-sm text-slate-400 py-12">
              No salary rows for {month}. Import a sheet to get started.
            </div>
          ) : (
          <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-200 bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left py-2 px-2 w-8">
                    <input type="checkbox"
                           checked={filtered.length > 0 && filtered.filter(r => r.pending > 0.01).every(r => selected[r.employee_id]?.checked)}
                           onChange={(e) => toggleAll(e.target.checked)}
                           data-testid="cf-sal-select-all" />
                  </th>
                  <th className="text-left py-2 px-2">E-Code</th>
                  <th className="text-left py-2 px-2">Name</th>
                  <th className="text-left py-2 px-2">Department</th>
                  <th className="text-right py-2 px-2">Net Pay</th>
                  <th className="text-right py-2 px-2">Paid</th>
                  <th className="text-right py-2 px-2">Pending</th>
                  <th className="text-right py-2 px-2 w-32">Pay now ₹</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const cur = selected[r.employee_id] || {};
                  const isFully = r.pending <= 0.01;
                  return (
                    <tr key={r.employee_id} data-testid={`cf-sal-row-${r.employee_id}`}
                        className={`border-b border-slate-100 hover:bg-slate-50 ${isFully ? 'opacity-60' : ''}`}>
                      <td className="py-2 px-2">
                        <input type="checkbox" checked={!!cur.checked} onChange={() => toggleRow(r)}
                               disabled={isFully} data-testid={`cf-sal-check-${r.employee_id}`} />
                      </td>
                      <td className="py-2 px-2 font-mono text-xs">{r.employee_id}</td>
                      <td className="py-2 px-2 font-medium text-slate-800">{r.name}</td>
                      <td className="py-2 px-2 text-slate-600">{r.department || '—'}</td>
                      <td className="py-2 px-2 text-right">{INR(r.net_pay)}</td>
                      <td className="py-2 px-2 text-right text-emerald-700">{INR(r.paid)}</td>
                      <td className={`py-2 px-2 text-right font-semibold ${isFully ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {isFully ? 'Fully paid' : INR(r.pending)}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {!isFully && (
                          <input type="number" min="0" max={r.pending}
                                 value={cur.checked ? (cur.amount ?? '') : ''}
                                 onChange={(e) => setAmount(r.employee_id, e.target.value)}
                                 disabled={!cur.checked}
                                 data-testid={`cf-sal-amt-${r.employee_id}`}
                                 className="w-24 px-2 py-1 text-sm border border-slate-200 rounded text-right disabled:bg-slate-50" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showPay && (
        <SalaryBulkPayModal
          month={month}
          items={selectedItems.map(r => ({
            employee_id: r.employee_id,
            name: r.name,
            amount: Number(selected[r.employee_id]?.amount) || 0,
            pending: r.pending,
          }))}
          categories={categories}
          onClose={() => setShowPay(false)}
          onSaved={async () => { setShowPay(false); setSelected({}); await reload(); }}
        />
      )}

      {importReport && (
        <SalaryImportReportModal report={importReport} onClose={() => setImportReport(null)} />
      )}
    </div>
  );
}

function SalaryBulkPayModal({ month, items, categories, onClose, onSaved }) {
  const [plannedDate, setPlannedDate] = useState(iso(new Date()));
  const [categoryId, setCategoryId] = useState(categories[0]?.id || '');
  const [saving, setSaving] = useState(false);

  const valid = items.filter(i => i.amount > 0 && i.amount <= i.pending);
  const total = valid.reduce((s, i) => s + i.amount, 0);
  const invalidCount = items.length - valid.length;

  const submit = async (e) => {
    e.preventDefault();
    if (!categoryId) return toast.error('Pick a category');
    if (valid.length === 0) return toast.error('No valid amounts entered');
    setSaving(true);
    try {
      const res = await api.cashflowBulkSalaryPay({
        month,
        planned_date: plannedDate,
        category_id: categoryId,
        items: valid.map(i => ({ employee_id: i.employee_id, amount: i.amount })),
      });
      if (res.data.over_budget_warning) {
        toast.warning(`Added ${res.data.created} entries — over the daily limit for ${plannedDate}`);
      } else {
        toast.success(`Added ${res.data.created} salary entries to calendar`);
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cf-sal-pay-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-heading text-lg flex items-center gap-2"><UsersIcon size={18} /> Pay {valid.length} salaries · {INR(total)}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          {invalidCount > 0 && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">
              {invalidCount} row(s) skipped — amount is 0 or exceeds pending balance.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Payment date</label>
              <input type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)}
                     className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Category</label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
                      data-testid="cf-sal-pay-category"
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white">
                <option value="">— pick —</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-slate-600 uppercase mb-2">Preview</h4>
            <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-md">
              {valid.map(i => (
                <div key={i.employee_id} className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100 last:border-0 text-sm">
                  <span className="text-slate-700 truncate">
                    <span className="font-mono text-xs text-slate-400 mr-2">{i.employee_id}</span>
                    {i.name}
                  </span>
                  <span className="font-semibold">{INR(i.amount)}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-[11px] text-slate-500">
            One calendar entry will be created per employee on {plannedDate}. The paid amount on the salary row
            updates <b>only</b> when you tick the entry as done in the calendar.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
          <button type="submit" disabled={saving || valid.length === 0} data-testid="cf-sal-pay-save"
                  className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50">
            {saving ? 'Adding…' : `Add ${valid.length} to Cash Flow`}
          </button>
        </div>
      </form>
    </div>
  );
}

function SalaryImportReportModal({ report, onClose }) {
  const errs = report.errors || [];
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cf-sal-import-report">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-heading text-lg">Salary import — {report.month}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="p-3 bg-slate-50 border border-slate-200 rounded">
              <div className="text-2xl font-bold text-slate-800">{report.salary_rows}</div>
              <div className="text-[10px] uppercase text-slate-500 tracking-wide">Salary rows</div>
            </div>
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
              <div className="text-2xl font-bold text-emerald-700">{report.new_employees}</div>
              <div className="text-[10px] uppercase text-emerald-700 tracking-wide">New employees</div>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded">
              <div className="text-2xl font-bold text-blue-700">{report.updated_employees}</div>
              <div className="text-[10px] uppercase text-blue-700 tracking-wide">Updated</div>
            </div>
            <div className="p-3 bg-red-50 border border-red-200 rounded">
              <div className="text-2xl font-bold text-red-700">{report.error_count}</div>
              <div className="text-[10px] uppercase text-red-700 tracking-wide">Skipped</div>
            </div>
          </div>
          {errs.length > 0 && (
            <div>
              <h4 className="text-xs uppercase font-semibold text-slate-600 mb-2">Rows that need fixing</h4>
              <ul className="text-sm space-y-1 max-h-64 overflow-y-auto border border-slate-200 rounded p-2 bg-slate-50">
                {errs.map((e, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-700">
                    <span className="font-mono text-xs text-slate-400 shrink-0">Row {e.row}:</span>
                    <span>{e.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-slate-900 text-white rounded-md hover:bg-slate-800">Done</button>
        </div>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────────────
// Consolidated View — Admin + Finance Controller
// ─────────────────────────────────────────────────────────────────────────────
function ConsolidatedTab({ managers, categories }) {
  const [scope, setScope] = useState('today'); // today | month | next30 | all_pending | custom | all
  const [status, setStatus] = useState('all'); // pending | paid | all
  const [managerId, setManagerId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [toggling, setToggling] = useState({});

  const buildParams = () => {
    const p = { scope, status };
    if (managerId) p.manager_id = managerId;
    if (categoryId) p.category_id = categoryId;
    if (scope === 'custom' && fromDate && toDate) {
      p.from_date = fromDate;
      p.to_date = toDate;
    }
    if (scope === 'single' && fromDate) {
      p.from_date = fromDate;
    }
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.cashflowConsolidated(buildParams());
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load consolidated view');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [scope, status, managerId, categoryId, fromDate, toDate]);

  const togglePaid = async (entry) => {
    setToggling(t => ({ ...t, [entry.id]: true }));
    try {
      await api.cashflowToggleDone(entry.id);
      await load();
    } catch (e) {
      toast.error('Failed to update');
    } finally {
      setToggling(t => {
        const next = { ...t };
        delete next[entry.id];
        return next;
      });
    }
  };

  const downloadSheet = async () => {
    setDownloading(true);
    try {
      const res = await api.cashflowConsolidatedExport(buildParams());
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cashflow_consolidated_${scope}_${iso(new Date())}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Sheet downloaded');
    } catch (e) {
      toast.error('Download failed');
    } finally { setDownloading(false); }
  };

  const scopeLabel = {
    today: 'Today',
    single: 'Single Date',
    month: 'This Month',
    next30: 'Next 30 Days',
    all_pending: 'All Pending',
    custom: 'Custom Range',
    all: 'All Entries',
  };

  return (
    <div className="space-y-4" data-testid="cf-consolidated">
      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1" data-testid="cf-scope-tabs">
            {['today', 'single', 'month', 'next30', 'all_pending', 'custom', 'all'].map(s => (
              <button
                key={s}
                onClick={() => setScope(s)}
                data-testid={`cf-scope-${s}`}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                  scope === s
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {scopeLabel[s]}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          <button
            onClick={downloadSheet}
            disabled={downloading || !data || (data?.count || 0) === 0}
            data-testid="cf-download-sheet"
            className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            <Download size={14} /> {downloading ? 'Preparing…' : 'Download Sheet'}
          </button>
        </div>

        {scope === 'single' && (
          <div className="flex flex-wrap gap-2 items-center border-t border-slate-100 pt-3">
            <label className="text-xs text-slate-500 font-semibold uppercase">Pick a date</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                   data-testid="cf-single-date"
                   className="px-2 py-1.5 text-sm border border-slate-200 rounded" />
          </div>
        )}

        {scope === 'custom' && (
          <div className="flex flex-wrap gap-2 items-center border-t border-slate-100 pt-3">
            <label className="text-xs text-slate-500 font-semibold uppercase">From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                   className="px-2 py-1.5 text-sm border border-slate-200 rounded" />
            <label className="text-xs text-slate-500 font-semibold uppercase">To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                   className="px-2 py-1.5 text-sm border border-slate-200 rounded" />
          </div>
        )}

        <div className="flex flex-wrap gap-2 items-center border-t border-slate-100 pt-3">
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 font-semibold uppercase">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                    data-testid="cf-status-filter"
                    disabled={scope === 'all_pending'}
                    className="px-2 py-1.5 text-sm border border-slate-200 rounded bg-white disabled:opacity-50">
              <option value="all">All</option>
              <option value="pending">Pending only</option>
              <option value="paid">Paid only</option>
            </select>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 font-semibold uppercase">Manager</span>
            <select value={managerId} onChange={(e) => setManagerId(e.target.value)}
                    data-testid="cf-manager-filter"
                    className="px-2 py-1.5 text-sm border border-slate-200 rounded bg-white">
              <option value="">All managers</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 font-semibold uppercase">Category</span>
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
                    data-testid="cf-category-filter"
                    className="px-2 py-1.5 text-sm border border-slate-200 rounded bg-white">
              <option value="">All</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex-1" />
          <button onClick={load} className="p-1.5 hover:bg-slate-100 rounded" title="Refresh">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-[10px] uppercase text-slate-500 tracking-wide">Total Entries</div>
            <div className="text-2xl font-bold text-slate-900" data-testid="cf-consol-count">{data.count}</div>
            <div className="text-xs text-slate-500">{INR(data.total)}</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="text-[10px] uppercase text-amber-700 tracking-wide">Pending</div>
            <div className="text-2xl font-bold text-amber-700" data-testid="cf-consol-pending">{data.pending_count}</div>
            <div className="text-xs text-amber-700">{INR(data.pending_amount)}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="text-[10px] uppercase text-emerald-700 tracking-wide">Paid</div>
            <div className="text-2xl font-bold text-emerald-700" data-testid="cf-consol-paid">{data.paid_count}</div>
            <div className="text-xs text-emerald-700">{INR(data.paid_amount)}</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-[10px] uppercase text-slate-500 tracking-wide">Managers</div>
            <div className="text-2xl font-bold text-slate-900">{data.by_manager?.length || 0}</div>
            <div className="text-xs text-slate-500">with entries</div>
          </div>
        </div>
      )}

      {/* Per-manager breakdown */}
      {data && data.by_manager?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <div className="text-xs uppercase text-slate-500 font-semibold tracking-wide mb-2">By Manager</div>
          <div className="flex flex-wrap gap-2">
            {data.by_manager.map(m => (
              <button
                key={m.manager_id}
                onClick={() => setManagerId(managerId === m.manager_id ? '' : m.manager_id)}
                data-testid={`cf-consol-mgr-${m.manager_id}`}
                className={`px-3 py-2 text-xs rounded border transition-colors ${
                  managerId === m.manager_id
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white border-slate-200 hover:bg-slate-50'
                }`}
              >
                <div className="font-semibold">{m.manager_name}</div>
                <div className="text-[10px] opacity-80">
                  {m.count} · {INR(m.total)}
                  {m.pending > 0 && <span className={`ml-1 ${managerId === m.manager_id ? 'text-amber-300' : 'text-amber-600'}`}>· pending {INR(m.pending)}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Entries table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading && <div className="text-center text-xs text-slate-500 py-6">Loading…</div>}
        {!loading && (data?.entries?.length || 0) === 0 && (
          <div className="text-center text-sm text-slate-400 py-10">No entries match the current filters.</div>
        )}
        {!loading && data?.entries?.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cf-consol-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 px-3">Date</th>
                  <th className="text-left py-2 px-3">Manager</th>
                  <th className="text-left py-2 px-3">Title</th>
                  <th className="text-left py-2 px-3">Category</th>
                  <th className="text-left py-2 px-3">Vendor</th>
                  <th className="text-right py-2 px-3">Amount</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th className="text-left py-2 px-3">Marked By</th>
                  <th className="text-right py-2 px-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map(e => {
                  const isDone = !!e.done;
                  const busy = !!toggling[e.id];
                  return (
                    <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cf-consol-row-${e.id}`}>
                      <td className="py-2 px-3 text-slate-800 whitespace-nowrap">{e.planned_date}</td>
                      <td className="py-2 px-3 text-slate-700">{e.manager_name || '—'}</td>
                      <td className="py-2 px-3 text-slate-800">
                        <div>{e.title}</div>
                        {e.notes && <div className="text-[11px] text-slate-500 truncate max-w-xs">{e.notes}</div>}
                      </td>
                      <td className="py-2 px-3">
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.category_color || '#94a3b8' }} />
                          {e.category_name || '—'}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-slate-600">{e.vendor_name || '—'}</td>
                      <td className="py-2 px-3 text-right font-semibold text-slate-900 whitespace-nowrap">{INR(e.amount)}</td>
                      <td className="py-2 px-3">
                        {isDone ? (
                          <span className="text-[11px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded font-semibold">Paid</span>
                        ) : (
                          <span className="text-[11px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded font-semibold">Pending</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-[11px] text-slate-500">
                        {e.done_by_name || '—'}
                        {e.done_at && <div className="text-[10px] text-slate-400">{new Date(e.done_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</div>}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <button
                          onClick={() => togglePaid(e)}
                          disabled={busy}
                          data-testid={`cf-consol-toggle-${e.id}`}
                          className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                            isDone
                              ? 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                              : 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                          } disabled:opacity-50`}
                        >
                          {busy ? '…' : (isDone ? 'Mark Pending' : 'Mark Paid')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Audit Log tab (Admin only) — trail of every entry mutation
// ─────────────────────────────────────────────────────────────────────────────
function AuditLogTab({ managers }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [action, setAction] = useState('');
  const [actorId, setActorId] = useState('');
  const [managerId, setManagerId] = useState('');
  const [fromTs, setFromTs] = useState('');
  const [toTs, setToTs] = useState('');
  const [limit, setLimit] = useState(200);
  const [expanded, setExpanded] = useState({});

  const buildParams = () => {
    const p = { limit };
    if (action) p.action = action;
    if (actorId) p.actor_id = actorId;
    if (managerId) p.manager_id = managerId;
    if (fromTs) p.from_ts = fromTs;
    if (toTs) p.to_ts = toTs;
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.cashflowAudit(buildParams());
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load audit log');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [action, actorId, managerId, fromTs, toTs, limit]);

  const downloadSheet = async () => {
    setDownloading(true);
    try {
      const res = await api.cashflowAuditExport(buildParams());
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cashflow_audit_${iso(new Date())}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Audit sheet downloaded');
    } catch (e) {
      toast.error('Download failed');
    } finally { setDownloading(false); }
  };

  const actionBadge = (a) => {
    const styles = {
      create: 'bg-emerald-100 text-emerald-700',
      update: 'bg-blue-100 text-blue-700',
      delete: 'bg-red-100 text-red-700',
      toggle_done: 'bg-purple-100 text-purple-700',
      bulk_mark_done: 'bg-purple-100 text-purple-700',
      bulk_shift: 'bg-amber-100 text-amber-700',
      bulk_delete: 'bg-red-100 text-red-700',
    };
    return `text-[10px] px-2 py-0.5 rounded font-semibold uppercase ${styles[a] || 'bg-slate-100 text-slate-700'}`;
  };

  const roleBadge = (r) => {
    const styles = {
      admin: 'bg-slate-900 text-white',
      finance_controller: 'bg-violet-600 text-white',
      manager: 'bg-slate-200 text-slate-800',
    };
    return `text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wide ${styles[r] || 'bg-slate-100 text-slate-700'}`;
  };

  const fmtChange = (chg) => {
    if (!chg || Object.keys(chg).length === 0) return null;
    const parts = [];
    for (const [k, v] of Object.entries(chg)) {
      if (v && typeof v === 'object' && 'old' in v && 'new' in v) {
        parts.push({ field: k, old: String(v.old ?? '—'), new: String(v.new ?? '—') });
      } else {
        parts.push({ field: k, old: '', new: JSON.stringify(v) });
      }
    }
    return parts;
  };

  return (
    <div className="space-y-4" data-testid="cf-audit">
      <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 font-semibold uppercase">Action</span>
            <select value={action} onChange={(e) => setAction(e.target.value)}
                    data-testid="cf-audit-action" className="px-2 py-1.5 text-sm border border-slate-200 rounded bg-white">
              <option value="">All</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="toggle_done">Toggle Paid</option>
              <option value="bulk_mark_done">Bulk Mark Paid</option>
              <option value="bulk_shift">Bulk Shift</option>
              <option value="bulk_delete">Bulk Delete</option>
            </select>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 font-semibold uppercase">Manager</span>
            <select value={managerId} onChange={(e) => setManagerId(e.target.value)}
                    className="px-2 py-1.5 text-sm border border-slate-200 rounded bg-white">
              <option value="">All</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 font-semibold uppercase">Actor</span>
            <select value={actorId} onChange={(e) => setActorId(e.target.value)}
                    className="px-2 py-1.5 text-sm border border-slate-200 rounded bg-white">
              <option value="">Anyone</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="flex-1" />
          <button onClick={load} className="p-1.5 hover:bg-slate-100 rounded" title="Refresh">
            <RefreshCw size={14} />
          </button>
          <button
            onClick={downloadSheet}
            disabled={downloading || rows.length === 0}
            data-testid="cf-audit-download"
            className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            <Download size={14} /> {downloading ? 'Preparing…' : 'Download Log'}
          </button>
        </div>
        <div className="flex flex-wrap gap-2 items-center border-t border-slate-100 pt-3">
          <label className="text-xs text-slate-500 font-semibold uppercase">From</label>
          <input type="datetime-local" value={fromTs} onChange={(e) => setFromTs(e.target.value)}
                 className="px-2 py-1.5 text-sm border border-slate-200 rounded" />
          <label className="text-xs text-slate-500 font-semibold uppercase">To</label>
          <input type="datetime-local" value={toTs} onChange={(e) => setToTs(e.target.value)}
                 className="px-2 py-1.5 text-sm border border-slate-200 rounded" />
          <div className="flex-1" />
          <label className="text-xs text-slate-500 font-semibold uppercase">Limit</label>
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}
                  className="px-2 py-1.5 text-sm border border-slate-200 rounded bg-white">
            <option value={50}>50</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
            <option value={2000}>2000</option>
          </select>
        </div>
      </div>

      <div className="text-xs text-slate-500">
        Showing <b>{rows.length}</b> of <b>{total}</b> log entries.
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading && <div className="text-center text-xs text-slate-500 py-6">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="text-center text-sm text-slate-400 py-10">No audit entries match these filters.</div>
        )}
        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cf-audit-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 px-3">When</th>
                  <th className="text-left py-2 px-3">Action</th>
                  <th className="text-left py-2 px-3">Actor</th>
                  <th className="text-left py-2 px-3">Manager</th>
                  <th className="text-left py-2 px-3">Entry</th>
                  <th className="text-left py-2 px-3">Changes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const snap = r.entry_snapshot || {};
                  const chg = fmtChange(r.changes);
                  const isOpen = !!expanded[r.id];
                  return (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 align-top" data-testid={`cf-audit-row-${r.id}`}>
                      <td className="py-2 px-3 whitespace-nowrap text-slate-700">
                        {r.timestamp ? new Date(r.timestamp).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                      </td>
                      <td className="py-2 px-3">
                        <span className={actionBadge(r.action)}>{(r.action || '').replace('_', ' ')}</span>
                      </td>
                      <td className="py-2 px-3">
                        <div className="text-slate-800">{r.actor_name || '—'}</div>
                        <span className={roleBadge(r.actor_role)}>{r.actor_role || '—'}</span>
                      </td>
                      <td className="py-2 px-3 text-slate-700">{r.manager_name || '—'}</td>
                      <td className="py-2 px-3">
                        <div className="text-slate-800">{snap.title || <span className="text-slate-400">(deleted)</span>}</div>
                        <div className="text-[11px] text-slate-500">
                          {snap.amount ? INR(snap.amount) : ''}
                          {snap.planned_date && <> · {snap.planned_date}</>}
                          {snap.emp_code && <> · {snap.emp_code}</>}
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        {chg && chg.length > 0 ? (
                          <>
                            <button
                              onClick={() => setExpanded(x => ({ ...x, [r.id]: !isOpen }))}
                              className="text-[11px] text-blue-600 hover:underline"
                            >
                              {isOpen ? 'Hide' : `${chg.length} change(s)`}
                            </button>
                            {isOpen && (
                              <ul className="mt-1 space-y-0.5 text-[11px] font-mono text-slate-600">
                                {chg.map((c, i) => (
                                  <li key={i}>
                                    <b>{c.field}:</b>{' '}
                                    {c.old !== '' && <span className="line-through text-slate-400">{c.old}</span>}
                                    {c.old !== '' && ' → '}
                                    <span className="text-slate-900">{c.new}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </>
                        ) : (
                          <span className="text-[11px] text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

